import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useNavigate } from 'react-router-dom';
import { getComparator, stableSort } from './data';
import { Order } from './types';
import { RootState } from '../../redux/store';
import { useSelector } from 'react-redux';
import { ILeague } from '../../types';
import { EnhancedTableToolbar } from './TableToolBar';
import { TableMatch } from './TableMatch';
import styles from './table.module.css';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import PhotoSizeSelectActualTwoToneIcon from '@mui/icons-material/PhotoSizeSelectActualTwoTone';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import EmojiEventsTwoToneIcon from '@mui/icons-material/EmojiEventsTwoTone';
import SportsSoccerTwoToneIcon from '@mui/icons-material/SportsSoccerTwoTone';
import clsx from 'clsx';

export default function EnhancedTable({ isActivated }: { isActivated: boolean }) {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof ILeague>("id");
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [row, setRow] = React.useState<ILeague>();
  const navigate = useNavigate();
  const { leagues } = useSelector((state: RootState) => state.leagues);

  const filterLeagues = leagues?.map((item: any) => {
    const { ["id"]: id, ["createdDate"]: createdDate, ...fitlerLeag } = item;

    return fitlerLeag;
  });

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof ILeague) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filterLeagues.map((n: any) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };
  
  const handleClick = (
    event: React.MouseEvent<unknown>,
    name: string,
    row: any,
    index: number
  ) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(name);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setRow(leagues[index]);
    setSelected(newSelected);
    navigate(`${leagues[index].id}`,{state: leagues[index]});
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const array: any = Array.from(new Set(filterLeagues.map((item: any) => Object.keys(item)).flat()));
  
  const handleTableContent = (rowItem: any, rowType: any) => {
    switch (rowType) {
      case 'isPaid':
      case 'activated': {
        return rowItem ? <CheckCircleTwoToneIcon color="success" /> : <DoDisturbOnTwoToneIcon color="error" />
      }
      case 'color': {
        return <div style={{ backgroundColor: rowItem as string }} className={styles.tableItemColorContainer} />
      }
      case 'image': {
        return rowItem?.url ?
          <img src={rowItem?.url} alt={rowItem?.id} className="w-10 h-auto rounded-md" /> :
          <PhotoSizeSelectActualTwoToneIcon color="info" />
      }
      case 'region': {
        return <span>{typeof rowItem === 'object' ? rowItem?.name : ''}</span>
      }
      case 'localizedName': {
        return <span>{typeof rowItem === 'object' ? JSON.stringify(rowItem) : ''}</span>
      }
      case 'localizedShortName': {
        return <span>{typeof rowItem === 'object' ? JSON.stringify(rowItem) : ''}</span>
      }
      case 'type': {
        return (
          <div className="flex items-center">
            {(rowItem === 'championship' || rowItem === 'tournament') && (
              rowItem === 'championship' ?
              <EmojiEventsTwoToneIcon color="warning" /> :
              <SportsSoccerTwoToneIcon color="info" />
              )
            }
          </div>
        )
      }
      default: return <span className={rowType !== 'imageUrl' ? "capitalize" : ""}>{rowItem as string}</span>
    }
  }
  
  const handleTooltip = (value: any, valueType: string) => {
    switch (valueType) {
      case 'image': {
        return typeof value === 'object' ? value?.url : '';
      }
      case 'region': {
        return typeof value === 'object' ? value?.name : '';
      }
      case 'localizedName': {
        return typeof value === 'object' ? JSON.stringify(value) : '';
      }
      case 'localizedShortName': {
        return typeof value === 'object' ? JSON.stringify(value) : '';
      }
      default: return value?.toString()?.toUpperCase();
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar league={row} numSelected={selected.length} />
        <TableContainer sx={{ maxHeight: "60vh" }}>
          <Table stickyHeader sx={{ minWidth: 500 }} aria-labelledby="tableTitle" size={"small"}>
            <TableMatch
              isActivated={isActivated}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filterLeagues?.length}
              leagues={filterLeagues}
            />
            <TableBody>
              {stableSort(filterLeagues as any, getComparator(order, orderBy))
                .map((row, index) => {
                  const isItemSelected = isSelected(row.name as any);
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.name as any, row, index)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.name}
                      selected={isItemSelected}
                      className="hover:cursor-pointer"
                    >
                      {array.map((item: keyof typeof row) => {
                        const canView = item !== 'imageUrl';
                        const columnWidth = item === 'name' ? 'w-[200px]' : 'w-[50px]';
                        
                        return (canView &&
                          <TableCell variant="body">
                            <div
                              className={clsx('p-[5px]', columnWidth)}
                            >
                              <Tooltip title={handleTooltip(row[item], item)} placement="top">
                                {handleTableContent(row[item], item)}
                              </Tooltip>
                            </div>
                          </TableCell>
                          )
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}